import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  makeStyles,
  Box,
  FormControlLabel,
  Typography,
} from '@material-ui/core';
import { FilterNone } from '@material-ui/icons';
import { Button, useToast } from '@redshelf/redshelf-ui';
import IconButton from 'components/IconButton';
import {
  CheckmarkSwitch as ShareNotesSwitch,
  ConfirmationDialog,
  IconTooltip,
  ReadOnlyTextField,
} from 'components';
import {
  CollaborationModifiersContext,
  CollaborationValuesContext,
} from 'contexts';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      color: theme.palette.greyBlue[700],
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
      margin: theme.spacing(3, 2),
    },
    switchToggleLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: 0,
      minHeight: '40px',
      userSelect: 'none',
    },
    shareCodeContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    fieldAndButton: {
      display: 'flex',
      gap: theme.spacing(2),
      flexDirection: 'column',
      height: theme.spacing(12.5),
      justifyContent: 'space-between',
    },
    endAdornment: {
      padding: theme.spacing(1.25),
      transform: 'scaleY(-1)',
    },
    newCodeButton: {
      padding: 0,
      height: 'inherit',
      width: '100%',
    },
    note: {
      display: 'flex',
      marginTop: theme.spacing(2),
    },
    readOnlyField: {
      margin: '0 !important',
    },
  };
});

const SharingCodeField = ({ classes, sharingCode, handleCopyShareCode }) => {
  return (
    <ReadOnlyTextField
      id="sharing-code-field"
      className={`${classes.readOnlyField} noTranslate`}
      content={sharingCode}
      endAdornment={
        <IconTooltip id="copy-code" title="Copy Sharing Code" placement="top">
          <IconButton
            aria-label="Copy Sharing Code"
            className={classes.endAdornment}
          >
            <FilterNone />
          </IconButton>
        </IconTooltip>
      }
      label="Sharing Code"
      onClick={handleCopyShareCode}
    />
  );
};

const GenerateNewShareCodeConfirmationDialog = ({
  onCancel,
  onConfirm,
  isOpen,
}) => {
  return (
    <ConfirmationDialog
      cancel={{
        label: 'Never Mind',
        action: onCancel,
      }}
      confirm={{
        label: 'Generate New Code',
        action: onConfirm,
      }}
      id={'confirmGenerateSharingCodeDialog'}
      isOpen={isOpen}
      labelledById="generate-new-share-code-confirmation"
      message="Generating a new code will remove anyone's access that is using the previous code."
      title="Are you sure you want to proceed?"
      titleComponent="h4"
    />
  );
};

const ShareNotes = ({ ...props }) => {
  const classes = useStyles();
  const { enqueueToast } = useToast();

  const { generateNewSharingCode, pauseShare, resumeShare } = useContext(
    CollaborationModifiersContext,
  );
  const { share } = useContext(CollaborationValuesContext);

  const shareNotesSwitchLabelRef = useRef();

  const [sharingCode, setSharingCode] = useState(share?.sharingCode);
  const [shareNotesEnabled, setShareNotesEnabled] = useState(!share?.isPaused);
  const [isShareNotesUpdating, setIsShareNotesUpdating] = useState(false);
  const [
    isGenerateNewShareCodeDialogOpen,
    setIsGenerateNewShareCodeDialogOpen,
  ] = useState(false);

  const handleShareNotesSwitchOnChange = async (updatedShareNotesEnabled) => {
    if (isShareNotesUpdating) {
      return;
    }

    setIsShareNotesUpdating(true);

    try {
      if (updatedShareNotesEnabled) {
        await resumeShare();
      } else {
        await pauseShare();
      }
      setShareNotesEnabled(updatedShareNotesEnabled);
    } finally {
      setIsShareNotesUpdating(false);
      shareNotesSwitchLabelRef?.current.focus();
    }
  };

  const handleGenerateNewShareCodeOnClick = () => {
    setIsGenerateNewShareCodeDialogOpen(true);
  };

  const dialogOnCancel = () => {
    setIsGenerateNewShareCodeDialogOpen(false);
  };

  const dialogOnConfirm = async () => {
    await generateNewSharingCode();
    setIsGenerateNewShareCodeDialogOpen(false);
  };

  const handleCopyShareCode = () => {
    navigator.clipboard?.writeText(share.sharingCode);

    enqueueToast('Copied Sharing Code', {
      variant: 'success',
    });
  };

  useEffect(
    function updateSharingCode() {
      if (share?.sharingCode) {
        setSharingCode(share.sharingCode);
      }
    },
    [share?.sharingCode],
  );

  return (
    <div className={classes.root} {...props}>
      <Typography variant="body1">
        Collaboration allows you to share your notes. Simply turn on
        collaboration and anyone that enters the code that you provide will be
        able to see your notes.
      </Typography>
      <FormControlLabel
        className={classes.switchToggleLabel}
        ref={shareNotesSwitchLabelRef}
        control={
          <ShareNotesSwitch
            disabled={isShareNotesUpdating}
            isEnabled={shareNotesEnabled}
            onToggle={handleShareNotesSwitchOnChange}
            color="primary"
            name="shareNotesEnabledSwitch"
            inputProps={{
              'aria-label': `${
                shareNotesEnabled ? 'Disable' : 'Enable'
              } notes sharing`,
            }}
          />
        }
        label="Share my notes"
        labelPlacement="start"
      />
      {shareNotesEnabled && (
        <>
          <Box className={classes.shareCodeContainer}>
            <Box className={classes.fieldAndButton}>
              <SharingCodeField
                classes={classes}
                sharingCode={sharingCode}
                handleCopyShareCode={handleCopyShareCode}
              />
              <Button
                className={classes.newCodeButton}
                onClick={handleGenerateNewShareCodeOnClick}
                size="small"
                variant="secondary"
              >
                New Code
              </Button>
            </Box>
            <Typography className={classes.note} variant="body2">
              Note: Generating a new code will remove anyone's access that is
              using the previous code.
            </Typography>
          </Box>
          <GenerateNewShareCodeConfirmationDialog
            onCancel={dialogOnCancel}
            onConfirm={dialogOnConfirm}
            isOpen={isGenerateNewShareCodeDialogOpen}
          />
        </>
      )}
    </div>
  );
};

export default ShareNotes;
